'use client'

import * as React from 'react'
import { createPortal } from 'react-dom'
import { cn } from '@/lib/utils'
import { useIsDesktop } from '@/hooks/useIsDesktop'
import { Drawer, DrawerContent, DrawerFooter, DrawerHeader, DrawerTitle } from './drawer'

interface SlideOutPanelProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  children: React.ReactNode
  title?: string
  className?: string
  footer?: React.ReactNode
  direction?: 'left' | 'right'
}

export function SlideOutPanel({
  open,
  onOpenChange,
  children,
  title,
  className,
  footer,
  direction = 'right',
}: SlideOutPanelProps) {
  const [mounted, setMounted] = React.useState(false)
  const contentRef = React.useRef<HTMLDivElement>(null)
  const wasOpenRef = React.useRef(false)
  const isDesktop = useIsDesktop()

  React.useEffect(() => {
    setMounted(true)
  }, [])

  React.useEffect(() => {
    if (open) {
      // Only focus first input when panel is first opened
      if (!wasOpenRef.current) {
        const firstInput = contentRef.current?.querySelector('input:not([data-no-autofocus]), textarea, select')
        if (firstInput instanceof HTMLElement) {
          firstInput.focus()
        }
        wasOpenRef.current = true
      }

      // Handle escape key
      const handleEscape = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          onOpenChange(false)
        }
      }

      document.addEventListener('keydown', handleEscape)
      return () => document.removeEventListener('keydown', handleEscape)
    } else {
      wasOpenRef.current = false
    }
  }, [open, onOpenChange])

  if (!mounted) return null


  if (isDesktop) {

    return createPortal(
      <div
        className={cn(
          'fixed inset-0 z-[9999999]',
          open ? 'pointer-events-auto' : 'pointer-events-none'
        )}
      >
        {/* Backdrop */}
        <div
          className={cn(
            'fixed inset-0 bg-black/80 transition-opacity duration-200',
            open ? 'opacity-100' : 'opacity-0'
          )}
          onClick={() => onOpenChange(false)}
        />

        {/* Panel */}
        <div
          className={cn(
            'fixed transition-transform duration-200 ease-out',
            // Mobile: bottom-up slide with full width and top gap
            'bottom-0 left-0 right-0 h-[calc(100%-4rem)] translate-y-full md:translate-y-0',
            // Desktop: slide with max width - direction determines left/right positioning
            direction === 'left' 
              ? 'md:left-0 md:right-auto md:top-0 md:h-full md:w-full md:max-w-lg'
              : 'md:left-auto md:right-0 md:top-0 md:h-full md:w-full md:max-w-lg',
            // Apply transforms based on open state
            open 
              ? 'translate-y-0 md:translate-x-0'
              : direction === 'left'
                ? 'translate-y-full md:-translate-x-full'
                : 'translate-y-full md:translate-x-full',
            className
          )}
        >
          <div className="flex flex-col h-full">
            <div 
              className={cn('flex h-full flex-col overflow-hidden bg-atrium-inner text-atrium-black', {
                'shadow-lg': open,
                'border-l border-atrium-border': direction === 'right',
                'border-r border-atrium-border': direction === 'left',
              })}
            >
              {/* Header */}
              {title && (
                <div className="flex justify-between items-center px-6 py-4 bg-atrium-content">
                  <h2 className="text-base font-semibold">{title}</h2>
                </div>
              )}

              {/* Content */}
              <div ref={contentRef} className="flex min-h-0 flex-1 flex-col overflow-hidden">
                {children}
              </div>

              {/* Footer */}
              {footer && (
                <div className="px-6 py-4 border-t border-atrium-border bg-atrium-content">
                  {footer}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>,
      document.body
    )
  }

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent
        className={cn(
          'h-[calc(100%-4rem)] border-atrium-border bg-atrium-inner text-atrium-black [&>div:first-child]:bg-atrium-border',
          className
        )}
      >
        {title && (
          <DrawerHeader className="bg-atrium-inner px-6 py-4 text-center">
            <DrawerTitle className="text-xl font-semibold text-atrium-black">{title}</DrawerTitle>
          </DrawerHeader>
        )}
        <div ref={contentRef} className="flex min-h-0 flex-1 flex-col overflow-hidden">
          {children}
        </div>
        {footer && (
          <DrawerFooter className="border-t border-atrium-border bg-atrium-inner px-6 py-4">
            {footer}
          </DrawerFooter>
        )}
      </DrawerContent>
    </Drawer>
  )
}
